import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import Button from '../components/Button';
import { Field, FieldLabel, FieldInput } from '../components/Field';
import InputGroup from '../components/InputGroup';
import Loader from '../components/Loader';
import { Modal, ModalExit, ModalName, ModalPart } from '../components/Modal';
import Tooltip from '../components/Tooltip';
import { useToast } from '../components/Toast';
import { SPACE_JOIN_CODE } from '../utilities/queries';
import { RESET_SPACE_JOIN_CODE, TOGGLE_INVITES } from '../utilities/mutations';
import Icon from '../components/Icon';

const InviteLinkModal = () => {
  const params = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [resetSpaceJoinCode] = useMutation(RESET_SPACE_JOIN_CODE, {
    variables: { slug: params.s },
  });
  const [toggleInvites] = useMutation(TOGGLE_INVITES, {
    variables: { spaceSlug: params.s },
  });
  const { loading, data } = useQuery(SPACE_JOIN_CODE, {
    variables: { slug: params.s },
  });
  const [isLoading, setIsLoading] = useState(false);

  const close = () => !isLoading && navigate(`/s/${params.s}/settings/members`);

  const inviteLink = `${document.location.origin}/join/${data?.space?.joinCode}`;

  const [isCopied, setIsCopied] = useState(false);

  const clearCopiedLink = () => {
    setTimeout(() => {
      setIsCopied(false);
    }, 2500);
  };

  return (
    <Modal onClose={() => close()}>
      {loading ? (
        <ModalPart>
          <Loader />
        </ModalPart>
      ) : (
        <>
          <ModalExit />
          <ModalName>Invite link</ModalName>
          <ModalPart>
            {data?.space?.joinCode && (
              <>
                <Field>
                  <FieldLabel>Link</FieldLabel>
                  <InputGroup>
                    <FieldInput value={inviteLink} disabled />
                    {isCopied ? (
                      <Tooltip text="Copied">
                        <Button disabled>
                          <Icon name="check" />
                        </Button>
                      </Tooltip>
                    ) : (
                      <Tooltip text="Copy invite link">
                        <Button
                          data-autofocus
                          disabled={isLoading}
                          onClick={() => {
                            setIsCopied(true);
                            navigator.clipboard.writeText(inviteLink);
                            clearCopiedLink();
                          }}
                        >
                          <Icon name="link" />
                        </Button>
                      </Tooltip>
                    )}
                  </InputGroup>
                </Field>
                <Button
                  disabled={isLoading}
                  onClick={async () => {
                    try {
                      setIsLoading(true);
                      await resetSpaceJoinCode();
                      setIsLoading(false);
                      toast({ children: 'Invite link reset' });
                    } catch (error) {
                      setIsLoading(false);
                      toast({ children: error.message });

                      return;
                    }
                  }}
                  isExtended
                >
                  Reset code
                </Button>
              </>
            )}
            <Button
              disabled={isLoading}
              onClick={async () => {
                try {
                  setIsLoading(true);
                  const argData = await toggleInvites();
                  setIsLoading(false);
                  toast({
                    children: argData.data.toggleInvites?.joinCode
                      ? 'Invite link enabled'
                      : 'Invite link disabled',
                  });
                } catch (error) {
                  setIsLoading(false);
                  toast({ children: error.message });

                  return;
                }
              }}
              isExtended
            >
              {data?.space?.joinCode ? 'Disable invites' : 'Enable invites'}
            </Button>
          </ModalPart>
        </>
      )}
    </Modal>
  );
};

export default InviteLinkModal;
